/**
 * O histórico de e-mail arquivado por cópia, dentro da ficha do Negócio (8.5, FR-42, FR-31).
 *
 * **O e-mail continua sendo escrito fora do CRM**, e este cartão não tem botão de escrever nem de
 * responder: a v1 arquiva **por cópia** (§6 do PRD). O que faz uma linha nascer é o endereço próprio
 * do Negócio — `negocio-{codigo}@{domínio}` — posto em `To`, `Cc` ou `Bcc` no cliente de sempre;
 * uma varredura IMAP do `comunicacao-barramento` a recolhe e a vincula. Um botão aqui prometeria uma
 * capacidade que não existe, e a ausência é o recorte da história.
 *
 * **Quem enxerga cada linha é a RLS, e não esta tela.** A matriz é a do FR-31 — gestão vê tudo, o
 * Responsável vê o próprio Negócio, todo comercial vê o que está em Funil de Prospecção —, aplicada
 * linha a linha pela policy do serviço sobre a réplica local do Negócio. A tela não filtra nada: um
 * filtro aqui seria uma segunda cópia da regra, na camada que menos consegue garanti-la.
 *
 * **Negócio invisível responde `200` com lista vazia**, e nunca `404` — as duas causas ("não há
 * e-mail" e "a matriz não entrega") são a mesma resposta de propósito. Por isso o estado vazio deste
 * cartão é o mesmo nos dois casos, e ele **não** tenta explicar qual dos dois aconteceu: dizer "você
 * não pode ver" contaria a existência do que a matriz esconde.
 *
 * **Quem não tem `acessar_whatsapp` não vê o cartão**, e não vê um cartão vermelho. A permissão é
 * conveniência e não autorização — quem decide é o serviço, que responde `403` —, mas montar o
 * cartão para quem a teve negada punha um `role="alert"` vermelho em **toda** ficha de Negócio
 * daquela pessoa, todo dia, sobre a configuração funcionando exatamente como o Admin a quis. Um
 * alerta que sempre aparece deixa de ser alerta. A consulta também não sai: o hook recebe
 * `habilitada`, porque ele roda antes de qualquer `return null`.
 *
 * **Quatro estados de leitura, e os quatro estão escritos.** Falha do serviço vira a mensagem dele,
 * verbatim, em `role="alert"`; enquanto a resposta não chegou, um `role="status"` diz que está
 * carregando; lista vazia é o estado vazio; e a lista é a lista. Sem o terceiro, "não há e-mail" e
 * "ainda não sei" ficariam iguais — e a tela afirmaria ausência onde só há desconhecimento. É a
 * ordem canônica de `OrcamentoDoNegocio`, e ela é a mesma de propósito.
 *
 * **O corpo nasce recolhido**, num `<details>`. Um e-mail é longo por natureza — a thread inteira
 * vem citada abaixo do que a pessoa escreveu hoje —, e três deles abertos empurrariam o resto da
 * ficha para fora da tela. O que fica sempre visível é o que identifica a mensagem: remetente,
 * assunto e data. O `<details>` é do HTML e não de um estado em React: ele já abre e fecha por
 * conta, é acessível por teclado, e um `useState` por linha seria reescrever o que o navegador faz.
 *
 * **A data é a do remetente** (`recebido_em`, o cabeçalho `Date`), e não o instante em que o CRM
 * arquivou: é a data que a pessoa vê no próprio cliente de e-mail, e mostrar a outra faria uma
 * mensagem de ontem, varrida hoje, aparecer como de hoje. Ela é formatada por `DATA_E_HORA`,
 * importada de `modulos/negocios/apresentacao` — a **mesma** que a ficha usa nos carimbos dela: duas
 * cópias divergiriam no dia em que alguém acrescentasse os segundos, e o histórico de movimentações
 * diria uma coisa e este cartão, ao lado, outra.
 *
 * **Uma árvore só, e não duas.** Ao contrário de `OrcamentoDoNegocio`, aqui não há tabela: a lista é
 * de blocos empilhados nos dois viewports, e ela já cabe a 375 px sem rolagem horizontal. A paridade
 * da NFR-1 é de **função** — ler o histórico e abrir o corpo —, e as duas existem igualmente porque
 * a árvore é a mesma. Um `useEhCelular()` aqui seria uma virada sem nada do outro lado.
 *
 * **Não há `@media` no CSS deste componente**, pela regra do repositório: a virada de layout, quando
 * há, é de árvore. Aqui não há virada nenhuma.
 */

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useSessao } from '@/modulos/identidade/sessao'
import { DATA_E_HORA } from '@/modulos/negocios/apresentacao'
import { type EmailArquivado, useEmailsDoNegocio } from './api'
import './emails-do-negocio.css'

/** O nome acessível da seção — é ele que faz a `<section>` virar uma `region`. */
const ID_DO_TITULO = 'emails-do-negocio-titulo'

/**
 * A permissão que as duas leituras de e-mail do serviço exigem — a **mesma** das leituras de
 * conversa (catálogo fechado do FR-61-A).
 *
 * A presença na claim basta: só as não negadas viajam nela, e `limitada` passa pela guarda da rota
 * (o recorte de `limitada` é de linha, e não de rota). É o molde de `podeEditar` da ficha.
 */
export const PERMISSAO_DE_LER = 'acessar_whatsapp'

export const TITULO = 'E-mail'

/**
 * O selo do cartão, no molde do `Espelho · leitura` do Orçamento — e ele **descreve**, não promete.
 *
 * O que está abaixo dele é somente leitura, e nenhuma ação existe: não há rota de escrita no serviço
 * e não haverá nesta v1. Sem o selo, a ausência de um botão "escrever" pareceria uma tela pela
 * metade, em vez do recorte deliberado que ela é.
 */
export const SELO = 'Arquivado por cópia'

export const CARREGANDO = 'Carregando os e-mails…'

/**
 * O estado vazio, e ele diz as duas coisas que quem abriu precisa saber: não há e-mail arquivado
 * **neste** Negócio, e de onde ele viria.
 *
 * Ele não anuncia época nenhuma — anunciar uma dentro de um cartão que **já** mostra e-mail quando
 * existe diria que a funcionalidade não chegou, sobre a tela que a entrega. É a mesma troca que
 * `SEM_TAREFAS` fez na 4.2 e que o vazio do Orçamento fez na 5.3.
 *
 * E ele **não** explica a visibilidade: a rota devolve lista vazia tanto para "não há e-mail" quanto
 * para "a matriz não entrega este Negócio a você", de propósito. Dizer qual dos dois aconteceu
 * contaria a existência do que a matriz esconde.
 */
export const VAZIO =
  'Nenhum e-mail arquivado neste negócio. Ele aparece aqui quando alguém puser o endereço do ' +
  'negócio em cópia — o CRM não escreve e-mail.'

/** O traço do campo que a mensagem não trouxe. E-mail sem assunto existe, e sem `Cc` também. */
const VAZIO_DO_CAMPO = '—'

/** O rótulo do assunto ausente. Ele é uma frase, e não um traço: um assunto vazio é um fato. */
export const SEM_ASSUNTO = '(sem assunto)'

/** Os rótulos dos campos do corpo recolhido, escritos uma vez. */
export const ROTULOS = {
  para: 'Para',
  copia: 'Cópia',
  corpo: 'Mensagem',
  abrir: 'Ver a mensagem',
} as const

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/**
 * O instante do e-mail em `America/Sao_Paulo` (NFR-8), ou o texto cru quando ele não é uma data.
 *
 * O cru volta **verbatim** pela mesma razão de `dataPuraPorExtenso`: um texto inesperado na tela é
 * feio, e um `Invalid Date` é pior — ele parece um dado. O serviço promete `date-time`, e a única
 * forma de ele não ser é o contrato ter mudado de forma.
 */
export function instanteDoEmail(bruto: string): string {
  const instante = new Date(bruto)
  return Number.isNaN(instante.getTime()) ? bruto : DATA_E_HORA.format(instante)
}

function Email({ email }: { email: EmailArquivado }) {
  return (
    <li className="email__item">
      <div className="email__cabecalho">
        <span className="email__remetente">{email.remetente || VAZIO_DO_CAMPO}</span>
        <span className="email__data">{instanteDoEmail(email.recebido_em)}</span>
      </div>
      <p className="email__assunto">{email.assunto || SEM_ASSUNTO}</p>
      {/* O corpo nasce recolhido: um e-mail traz a thread inteira citada, e três abertos
          empurrariam o resto da ficha para fora da tela. `<details>` é do HTML — abre, fecha e
          responde ao teclado sem um `useState` por linha. */}
      <details className="email__detalhes">
        <summary className="email__resumo">{ROTULOS.abrir}</summary>
        <dl className="email__campos">
          <dt>{ROTULOS.para}</dt>
          <dd>{email.destinatarios || VAZIO_DO_CAMPO}</dd>
          <dt>{ROTULOS.copia}</dt>
          <dd>{email.copias || VAZIO_DO_CAMPO}</dd>
        </dl>
        {/* `white-space: pre-wrap` no CSS: o corpo é `text/plain`, e as quebras de linha que a
            pessoa escreveu são parte do que ela disse. Ele nunca é HTML — o serviço arquiva a parte
            de texto da mensagem, e é ela que chega aqui como string. */}
        <p className="email__corpo">{email.corpo}</p>
      </details>
    </li>
  )
}

export function EmailsDoNegocio({ negocioId }: { negocioId: string }) {
  const { data: sessao } = useSessao()
  /*
   * **Quem não tem a permissão não vê o cartão** — e não um cartão vermelho.
   *
   * A permissão é conveniência, e não autorização: quem decide é o serviço, que responde `403` a
   * quem não a tem. Mas montar o cartão para quem a teve negada transformava esse `403` esperado —
   * a configuração funcionando como o Admin quis — num `role="alert"` vermelho em **toda** ficha de
   * Negócio daquela pessoa, dia após dia. Um alerta que sempre aparece deixa de ser alerta, e o dia
   * da falha de verdade (o serviço fora do ar) é o dia em que ninguém olha. É o molde de
   * `podeAbrir`/`podeAlterar` de `RatDoNegocio`, aplicado ao cartão inteiro porque aqui não há ação
   * a esconder: o cartão **é** a leitura.
   */
  const podeLer = sessao !== undefined && PERMISSAO_DE_LER in sessao.permissoes
  const consulta = useEmailsDoNegocio(negocioId, { habilitada: podeLer })
  const emails = consulta.data?.itens ?? []

  if (!podeLer) {
    return null
  }

  return (
    <section className="ficha__card" aria-labelledby={ID_DO_TITULO}>
      <div className="ficha__card-topo">
        <span className="ficha__card-titulo" id={ID_DO_TITULO}>
          {TITULO}
        </span>
        <span className="ficha__selo">{SELO}</span>
      </div>
      <div className="ficha__card-corpo">
        {consulta.isError ? (
          /* Verbatim: a recusa do serviço é a que sabe explicar o caso. */
          <p className="ficha__erro" role="alert">
            {mensagemDe(consulta.error)}
          </p>
        ) : /* `|| consulta.data === undefined` fecha o terceiro estado: sem rede, a consulta fica
              **pausada** — nem erro, nem `isLoading` —, e sem esta metade a tela desenharia o
              estado vazio como se o Negócio não tivesse e-mail. */
        consulta.isLoading || consulta.data === undefined ? (
          <p className="ficha__ajuda" role="status">
            {CARREGANDO}
          </p>
        ) : emails.length === 0 ? (
          <p className="ficha__vazio">{VAZIO}</p>
        ) : (
          <ul className="email__lista">
            {emails.map((email) => (
              <Email key={email.id} email={email} />
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
